// src/store/thunks/planThunks.js

import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import planService from "../../../services/jewelleryPlan/planService";

// ==================== PLAN CRUD ====================

// Create plan
export const createPlanThunk = createAsyncThunk(
  "plans/createPlan",
  async (planData, { rejectWithValue }) => {
    try {
      const response = await planService.createPlan(planData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get all plans
export const getAllPlansThunk = createAsyncThunk(
  "plans/getAllPlans",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await planService.getAllPlans(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get plan by ID
export const getPlanByIdThunk = createAsyncThunk(
  "plans/getPlanById",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await planService.getPlanById(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get customer plans
export const getCustomerPlansThunk = createAsyncThunk(
  "plans/getCustomerPlans",
  async ({ customerId, filters }, { rejectWithValue }) => {
    try {
      const response = await planService.getCustomerPlans(customerId, filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== PLAN DETAILS ====================

// Get plan schedule
export const getPlanScheduleThunk = createAsyncThunk(
  "plans/getPlanSchedule",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await planService.getPlanSchedule(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get plan ledger
export const getPlanLedgerThunk = createAsyncThunk(
  "plans/getPlanLedger",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await planService.getPlanLedger(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== PLAN PAYMENTS ====================

// Record payment for plan
export const recordPlanPaymentThunk = createAsyncThunk(
  "plans/recordPayment",
  async ({ planId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await planService.recordPlanPayment(planId, paymentData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== PLAN PAUSE/RESUME ====================

// Pause plan
export const pausePlanThunk = createAsyncThunk(
  "plans/pausePlan",
  async ({ planId, pauseData }, { rejectWithValue }) => {
    try {
      const response = await planService.pausePlan(planId, pauseData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Resume plan
export const resumePlanThunk = createAsyncThunk(
  "plans/resumePlan",
  async ({ planId, resumeData }, { rejectWithValue }) => {
    try {
      const response = await planService.resumePlan(planId, resumeData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Get pause history
export const getPauseHistoryThunk = createAsyncThunk(
  "plans/getPauseHistory",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await planService.getPauseHistory(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Check pause eligibility
export const checkPauseEligibilityThunk = createAsyncThunk(
  "plans/checkPauseEligibility",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await planService.checkPauseEligibility(planId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Approve pause
export const approvePauseThunk = createAsyncThunk(
  "plans/approvePause",
  async ({ pauseId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await planService.approvePause(pauseId, approvalData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Reject pause
export const rejectPauseThunk = createAsyncThunk(
  "plans/rejectPause",
  async ({ pauseId, rejectionData }, { rejectWithValue }) => {
    try {
      const response = await planService.rejectPause(pauseId, rejectionData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ NEW: Get pending pause requests
export const getPendingPauseRequestsThunk = createAsyncThunk(
  "plans/getPendingPauseRequests",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await planService.getPendingPauseRequests(filters);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== UTILITY ACTIONS ====================

// Clear plan error
export const clearPlanError = createAction("plans/clearPlanError");

// ✅ NEW: Clear pause data
export const clearPauseData = createAction("plans/clearPauseData");

// ✅ NEW: Reset plan state
export const resetPlanState = createAction("plans/resetState");
